/**
 * Vorlesen in Abschnitten: Der erste Satz geht sofort an den Server, der nächste Abschnitt wird
 * geladen, während der aktuelle läuft. Geht der Server nicht (Modell falsch eingestellt,
 * Anbieter weg), liest der Browser selbst weiter — Qualität je nach Gerät, aber es bleibt nicht
 * still.
 */
import { speakKianVoice, type KianSpeechAudio } from '../../../lib/api/domains/kianVoice';
import { splitForSpeech } from './speakable';

export interface SpeechPlayerOptions {
  /** Über den Server (Sprachmodell) vorlesen — sonst gleich im Browser */
  server: boolean;
  onUsage?: (usage: Pick<KianSpeechAudio, 'chars' | 'costEur' | 'model'>) => void;
  /** Server-Stimme ausgefallen, der Browser liest weiter */
  onFallback?: (reason: string) => void;
}

/** Deutsche Stimme des Browsers — lokale zuerst: die bleiben auf dem Gerät. */
export function pickGermanVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const german = voices.filter((v) => v.lang?.toLowerCase().startsWith('de'));
  return german.find((v) => v.localService) ?? german[0] ?? null;
}

export class SpeechPlayer {
  private current: AudioBufferSourceNode | null = null;
  private abort: AbortController | null = null;
  private stopped = false;
  private serverFailed = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly opts: SpeechPlayerOptions,
  ) {}

  /** Liest vor; löst auf, wenn fertig oder unterbrochen. */
  async speak(text: string): Promise<void> {
    this.stopped = false;
    const chunks = splitForSpeech(text);
    if (!chunks.length) return;
    if (!this.opts.server || this.serverFailed) return this.speakInBrowser(chunks);

    const abort = new AbortController();
    this.abort = abort;
    const load = (chunk: string): Promise<AudioBuffer> =>
      speakKianVoice(chunk, abort.signal).then((res) => {
        this.opts.onUsage?.(res);
        // decodeAudioData übernimmt den Puffer — eine Kopie hält die Antwort unversehrt
        return this.ctx.decodeAudioData(res.audio.slice(0));
      });

    let next: Promise<AudioBuffer> | null = load(chunks[0]);
    for (let i = 0; i < chunks.length; i++) {
      if (this.stopped) return;
      let buffer: AudioBuffer;
      try {
        buffer = await next!;
      } catch (err) {
        if (this.stopped) return;
        this.serverFailed = true;
        this.opts.onFallback?.(err instanceof Error ? err.message : String(err));
        return this.speakInBrowser(chunks.slice(i));
      }
      next = i + 1 < chunks.length ? load(chunks[i + 1]) : null;
      // Fehler des vorgeladenen Abschnitts erst beim nächsten Durchlauf behandeln
      next?.catch(() => undefined);
      if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => {});
      await this.play(buffer);
    }
  }

  private play(buffer: AudioBuffer): Promise<void> {
    return new Promise((resolve) => {
      if (this.stopped) {
        resolve();
        return;
      }
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.ctx.destination);
      source.onended = () => {
        if (this.current === source) this.current = null;
        resolve();
      };
      this.current = source;
      source.start();
    });
  }

  private speakInBrowser(chunks: string[]): Promise<void> {
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
    if (!synth) return Promise.resolve();
    const voice = pickGermanVoice(synth.getVoices());
    return chunks.reduce<Promise<void>>(
      (prev, chunk) =>
        prev.then(
          () =>
            new Promise<void>((resolve) => {
              if (this.stopped) {
                resolve();
                return;
              }
              const utterance = new SpeechSynthesisUtterance(chunk);
              utterance.lang = 'de-DE';
              if (voice) utterance.voice = voice;
              utterance.onend = () => resolve();
              utterance.onerror = () => resolve();
              synth.speak(utterance);
            }),
        ),
      Promise.resolve(),
    );
  }

  /** Sofort verstummen (Unterbrechen, Beenden). */
  stop(): void {
    this.stopped = true;
    this.abort?.abort();
    this.abort = null;
    try {
      this.current?.stop();
    } catch {
      /* schon zu Ende */
    }
    this.current = null;
    if (typeof window !== 'undefined') window.speechSynthesis?.cancel();
  }
}
